import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { Tag, Progress } from "antd";
import {
    CheckCircleFilled, CloseCircleFilled,
    BookOutlined, CalendarOutlined, BankOutlined,
    TrophyOutlined, BarChartOutlined, UserOutlined, StarFilled,
} from "@ant-design/icons";
import SubHeader from "../../components/SubHeader/SubHeader";
import Loader from "../../components/Loader/Loader";
import { STUDENT_ASSESSMENT_BY_ID } from "../../utils/apiPath";
import { postApi } from "../../utils/apiService";
import { errorToast } from "../../services/ToastHelper";
import { formatDateDDMMYYYY } from "../../services/dateFormatter";
import "./StudentAssessmentResult.scss";

const MAX_RATING = 5;

const StudentAssessmentResult = () => {
    const { studentId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const [isLoading, setIsLoading] = useState(false);
    const [assessmentData, setAssessmentData] = useState(null);

    const { assessmentTypeName, gradeName, studentName: stateStudentName } = location.state || {};
    const displayStudentName = assessmentData?.studentName || stateStudentName;
    const currentSubTitle = [assessmentTypeName, gradeName, displayStudentName].filter(Boolean).join(" > ");

    const fetchAssessmentResult = async () => {
        setIsLoading(true);
        try {
            const { statusCode, data, message } = await postApi(
                STUDENT_ASSESSMENT_BY_ID,
                {},
                { params: { id: studentId } }
            );
            if (statusCode === 200) {
                setAssessmentData(data);
            } else {
                errorToast(message || "Failed to fetch assessment results");
            }
        } catch (err) {
            console.error(err);
            errorToast("An error occurred while fetching assessment results");
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (studentId) fetchAssessmentResult();
    }, [studentId]);

    const handleBack = () => navigate(-1);

    const handleDownload = () => {
        if (assessmentData?.studentReport) {
            window.open(assessmentData.studentReport, "_blank");
        }
    };

    if (isLoading && !assessmentData) return <Loader />;

    const d = assessmentData;

    // Rating out of 5: convert to 0-100 for progress bar display
    const ratingToPercent = (rating) => ((rating ?? 0) / MAX_RATING) * 100;

    const ratingColor = (rating) => {
        if (rating >= 4) return "#10b981";
        if (rating >= 2.5) return "#f59e0b";
        return "#ef4444";
    };

    const hasExtraInfo = d && (
        d.schoolName || d.parentName || d.assessmentName || d.subject ||
        d.format || d.durationInMin || d.scheduledDate || d.createdDate
    );

    const hasAnalysis = d && (
        d.studentPotentialIndexSummary || d.childLearningArchetypeSummary ||
        d.strengthAreas || d.improvementAreas
    );

    return (
        <div className="assessment-result-page">
            <SubHeader
                title="Assessment Result"
                subTitle={currentSubTitle}
                showBack={true}
                onBack={handleBack}
                showRight={!!d?.studentReport}
                rightActionLabel="Download Report"
                onRightClick={handleDownload}
            />

            {d && (
                <>
                    {/* ── Student Info Card ── */}
                    <div className="student-info-card">
                        <div className="profile-header">
                            <div className="profile-avatar">
                                {(d.studentName || "S")[0].toUpperCase()}
                            </div>
                            <div className="profile-info">
                                <h2 className="student-name">{d.studentName}</h2>
                                <div className="profile-tags">
                                    {d.className && (
                                        <span className="profile-tag">
                                            <BookOutlined /> {d.className}
                                        </span>
                                    )}
                                    {d.sectionName && (
                                        <span className="profile-tag">Section: {d.sectionName}</span>
                                    )}
                                    {d.branchName && (
                                        <span className="profile-tag">
                                            <BankOutlined /> {d.branchName}
                                        </span>
                                    )}
                                    {d.academicYear && (
                                        <span className="profile-tag">
                                            <CalendarOutlined /> {d.academicYear}
                                        </span>
                                    )}
                                    {d.rollNumber && (
                                        <span className="profile-tag">
                                            <UserOutlined /> Roll No: {d.rollNumber}
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Extra assessment / student details */}
                        {hasExtraInfo && (
                            <div className="extra-info-grid">
                                {d.schoolName && (
                                    <div className="info-item">
                                        <span className="info-label">School</span>
                                        <span className="info-value">{d.schoolName}</span>
                                    </div>
                                )}
                                {d.parentName && (
                                    <div className="info-item">
                                        <span className="info-label">Parent / Guardian</span>
                                        <span className="info-value">{d.parentName}</span>
                                    </div>
                                )}
                                {d.assessmentName && (
                                    <div className="info-item">
                                        <span className="info-label">Assessment</span>
                                        <span className="info-value">{d.assessmentName}</span>
                                    </div>
                                )}
                                {d.subject && (
                                    <div className="info-item">
                                        <span className="info-label">Subject</span>
                                        <span className="info-value">{d.subject}</span>
                                    </div>
                                )}
                                {d.format && (
                                    <div className="info-item">
                                        <span className="info-label">Format</span>
                                        <span className="info-value">{d.format}</span>
                                    </div>
                                )}
                                {d.durationInMin != null && (
                                    <div className="info-item">
                                        <span className="info-label">Duration</span>
                                        <span className="info-value">{d.durationInMin} min</span>
                                    </div>
                                )}
                                {d.scheduledDate && (
                                    <div className="info-item">
                                        <span className="info-label">Scheduled Date</span>
                                        <span className="info-value">{formatDateDDMMYYYY(d.scheduledDate)}</span>
                                    </div>
                                )}
                                {d.createdDate && (
                                    <div className="info-item">
                                        <span className="info-label">Assessment Date</span>
                                        <span className="info-value">{formatDateDDMMYYYY(d.createdDate)}</span>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Score summary row */}
                        <div className="score-summary-row">
                            {d.overallAverage != null && (
                                <div className="score-stat">
                                    <span className="stat-label">Overall Rating</span>
                                    <span className="stat-value rating">
                                        <StarFilled className="star-icon" />
                                        {d.overallAverage} <small>/ {MAX_RATING}</small>
                                    </span>
                                </div>
                            )}
                            {d.totalScore != null && d.maxScore != null && (
                                <div className="score-stat">
                                    <span className="stat-label">Total Score</span>
                                    <span className="stat-value score">
                                        {d.totalScore} <small>/ {d.maxScore}</small>
                                    </span>
                                </div>
                            )}
                            {d.interpretationBand && (
                                <div className="score-stat">
                                    <span className="stat-label">Interpretation</span>
                                    <span className="stat-value band">{d.interpretationBand}</span>
                                </div>
                            )}
                            {d.percentile != null && (
                                <div className="score-stat">
                                    <span className="stat-label">Percentile</span>
                                    <span className="stat-value percentile">{d.percentile}%ile</span>
                                </div>
                            )}
                            {d.rank != null && (
                                <div className="score-stat">
                                    <span className="stat-label">Rank</span>
                                    <span className="stat-value rank">#{d.rank}</span>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* ── Module-wise Performance ── */}
                    {d.moduleAverages?.length > 0 && (
                        <div className="perf-overview-section">
                            <div className="section-heading">
                                <BarChartOutlined className="sh-icon" />
                                <h3>Module-wise Performance</h3>
                            </div>
                            <div className="module-bars-grid">
                                {d.moduleAverages.map((mod) => (
                                    <div key={mod.moduleId} className="module-bar-card">
                                        <div className="mbc-header">
                                            <span className="mbc-name">{mod.moduleName}</span>
                                            <span className="mbc-rating" style={{ color: ratingColor(mod.average) }}>
                                                <StarFilled className="mbc-star" />
                                                {mod.average} / {MAX_RATING}
                                            </span>
                                        </div>
                                        <Progress
                                            percent={ratingToPercent(mod.average)}
                                            showInfo={false}
                                            strokeColor={ratingColor(mod.average)}
                                            trailColor="#e2e8f0"
                                            strokeWidth={8}
                                        />
                                        {mod.score != null && mod.maxScore != null && (
                                            <span className="mbc-score">Score: {mod.score} / {mod.maxScore}</span>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* ── Subject-wise Performance (if available) ── */}
                    {d.subjectAverages?.length > 0 && (
                        <div className="perf-overview-section">
                            <div className="section-heading">
                                <BookOutlined className="sh-icon" />
                                <h3>Subject-wise Performance</h3>
                            </div>
                            <div className="module-bars-grid">
                                {d.subjectAverages.map((subj, idx) => (
                                    <div key={subj.subjectId ?? idx} className="module-bar-card">
                                        <div className="mbc-header">
                                            <span className="mbc-name">{subj.subjectName}</span>
                                            <span className="mbc-rating" style={{ color: ratingColor(subj.average) }}>
                                                <StarFilled className="mbc-star" />
                                                {subj.average} / {MAX_RATING}
                                            </span>
                                        </div>
                                        <Progress
                                            percent={ratingToPercent(subj.average)}
                                            showInfo={false}
                                            strokeColor={ratingColor(subj.average)}
                                            trailColor="#e2e8f0"
                                            strokeWidth={8}
                                        />
                                        {subj.score != null && subj.maxScore != null && (
                                            <span className="mbc-score">Score: {subj.score} / {subj.maxScore}</span>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* ── Performance Analysis ── */}
                    {hasAnalysis && (
                        <div className="analysis-section">
                            <div className="section-heading">
                                <TrophyOutlined className="sh-icon" />
                                <h3>Performance Analysis</h3>
                            </div>
                            <div className="analysis-grid">
                                {d.studentPotentialIndexSummary && (
                                    <div className="analysis-card">
                                        <div className="ac-title">Potential Index Summary</div>
                                        <p className="ac-text">{d.studentPotentialIndexSummary}</p>
                                    </div>
                                )}
                                {d.childLearningArchetypeSummary && (
                                    <div className="analysis-card">
                                        <div className="ac-title">Learning Archetype Summary</div>
                                        <p className="ac-text">{d.childLearningArchetypeSummary}</p>
                                    </div>
                                )}
                                {d.strengthAreas && (
                                    <div className="analysis-card strength">
                                        <div className="ac-title">Strength Areas</div>
                                        <p className="ac-text">{d.strengthAreas}</p>
                                    </div>
                                )}
                                {d.improvementAreas && (
                                    <div className="analysis-card improvement">
                                        <div className="ac-title">Areas for Improvement</div>
                                        <p className="ac-text">{d.improvementAreas}</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* ── Detailed Module Questions (bottom) ── */}
                    <div className="modules-container">
                        {d.moduleAverages?.map((moduleAvg) => {
                            const moduleAnswers = d.answers?.filter(
                                (ans) => ans.moduleId === moduleAvg.moduleId
                            );
                            if (!moduleAnswers?.length) return null;

                            const answerIds = new Set(moduleAnswers.map((a) => a.questionId));
                            const topLevelAnswers = moduleAnswers.filter(
                                (ans) => !ans.parentQuestionId || !answerIds.has(ans.parentQuestionId)
                            );

                            return (
                                <div key={moduleAvg.moduleId} className="module-section">
                                    <div className="module-header">
                                        <h3>{moduleAvg.moduleName}</h3>
                                        <div className="module-meta">
                                            <span className="module-rating">
                                                <StarFilled className="meta-star" />
                                                {moduleAvg.average} / {MAX_RATING}
                                            </span>
                                            {moduleAvg.score != null && moduleAvg.maxScore != null && (
                                                <span className="module-score">
                                                    {moduleAvg.score} / {moduleAvg.maxScore}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                    <div className="questions-list">
                                        {topLevelAnswers.map((ans, idx) => (
                                            <QuestionResultItem
                                                key={ans.questionId}
                                                ans={ans}
                                                index={idx + 1}
                                                allAnswers={moduleAnswers}
                                            />
                                        ))}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </>
            )}
        </div>
    );
};

/* ─────────────────────────────────────────────────── */
/*  Question Result Item  (behaviour unchanged)        */
/* ─────────────────────────────────────────────────── */
const QuestionResultItem = ({ ans, index, allAnswers }) => {
    const childQuestions = allAnswers?.filter((child) => child.parentQuestionId === ans.questionId);
    const hasCorrectness = ans.isCorrect !== null && ans.isCorrect !== undefined;

    return (
        <div className={`question-item-wrapper ${ans.parentQuestionId ? "is-child" : ""}`}>
            <div className="question-item">
                <div className="question-title-row">
                    <div className="q-text">
                        <span className="q-index">{index}. </span>
                        <span className="q-content">{ans.questionText}</span>
                    </div>
                    <div className="q-meta">
                        {hasCorrectness && (
                            <span className={`status-tag ${ans.isCorrect ? "correct" : "incorrect"}`}>
                                {ans.isCorrect ? "Correct" : "Incorrect"}
                            </span>
                        )}
                        <Tag color="blue" className="score-tag">
                            Score: {ans.score}/{ans.maxScore}
                        </Tag>
                    </div>
                </div>

                {ans.questionMediaUrl && (
                    <div className="q-media-container">
                        <img src={ans.questionMediaUrl} alt="Question" className="q-image" />
                    </div>
                )}

                <div className="answer-section">
                    {ans.questionType === 3 || ans.questionType === 4 ? (
                        <div className="options-view">
                            {ans.options?.map((opt) => {
                                const isSelected =
                                    ans.questionType === 4
                                        ? ans.responseValues?.includes(opt.optionId)
                                        : ans.responseValue === opt.optionId;
                                return (
                                    <div
                                        key={opt.optionId}
                                        className={`option-row-view ${isSelected ? "selected" : ""} ${opt.isCorrect ? "is-correct" : ""}`}
                                    >
                                        <div className="option-indicator">
                                            {ans.questionType === 3 ? (
                                                <div className={`radio-mock ${isSelected ? "checked" : ""}`} />
                                            ) : (
                                                <div className={`checkbox-mock ${isSelected ? "checked" : ""}`} />
                                            )}
                                        </div>
                                        <div className="option-text">{opt.optionText}</div>
                                        <div className="option-status">
                                            {opt.isCorrect && <CheckCircleFilled style={{ color: "#52c41a" }} />}
                                            {isSelected && !opt.isCorrect && (
                                                <CloseCircleFilled style={{ color: "#ff4d4f" }} />
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    ) : (
                        <div className="text-response">
                            <span className="label">Student&apos;s Answer:</span>
                            <div className="value-box">{ans.responseText || "No response recorded"}</div>
                        </div>
                    )}

                    {ans.interpretation && (
                        <div className="interpretation-box">
                            <span className="label">Feedback / Interpretation:</span>
                            <p>{ans.interpretation}</p>
                        </div>
                    )}
                </div>
            </div>

            {childQuestions?.length > 0 && (
                <div className="child-questions-list">
                    {childQuestions.map((child, cIdx) => (
                        <QuestionResultItem
                            key={child.questionId}
                            ans={child}
                            index={`${index}.${cIdx + 1}`}
                            allAnswers={allAnswers}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};

export default StudentAssessmentResult;
